// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import { defineStore } from 'pinia';
import { getActivePinia } from 'pinia';
import { getQuery } from '@/utils/url';
import { isLoggedIn, setAuthTokens, getAccessToken } from 'axios-jwt';
import { isPlatform } from '@ionic/vue';
import { Playback } from 'playback';
import { Preferences } from '@capacitor/preferences';
import { useAudioStore } from '@/stores/useAudioStore';
import api, { login, logout } from '@/utils/api';
import canReachRealNetwork from '@/utils/checkCaptive'
import axios from 'axios';
import { 
  BiometricAuth, 
  BiometryType, 
  BiometryErrorType, 
  CheckBiometryResult,
  AuthenticateOptions
} from '@aparajita/capacitor-biometric-auth';

const ACTIVE_PROFILE_KEY = 'activeProfileId';
const PIN_ATTEMPTS_KEY = 'pinAttempts';
const PIN_LOCKOUT_TIME_KEY = 'pinLockoutTime';

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,
    redirect: {},
    activeProfileId: null,
    biometryInfo: null as CheckBiometryResult | null,
    pinAttempts: 0,
    pinLockoutTime: null,
    lockoutTimer: null,
  }),

  getters: {
    hasAuth: (state) => state.user !== null,
    hasRedirect: (state) => {
      const redirect = state.redirect || {};
      return Object.keys(redirect).some((key) => validRedirectKeys.includes(key));
    },
    profiles: (state) => state.user?.profiles || [],
    activeProfile: (state) => state.profiles.find((profile) => profile.id === state.activeProfileId) || null,
    biometryAvailable: (state) => state.biometryInfo?.isAvailable || false,
    biometryType: (state) => state.biometryInfo?.biometryType || BiometryType.none,
    isPinLocked: (state) => {
      if (!state.pinLockoutTime) return false;
      return new Date() < new Date(state.pinLockoutTime);
    },
  },

	actions: {
		async checkAuth() {
			let networkAvailable = await canReachRealNetwork()
			let savedAuth = (await Preferences.get({ key: 'auth' }))?.value;
			if (savedAuth) {
				savedAuth = JSON.parse(savedAuth)
				await Playback.setBearerToken({ bearerToken: `Bearer ${savedAuth.data.authorization.token}` })
			}
			if (!networkAvailable && savedAuth) {
				setAuthTokens({
					accessToken: savedAuth.data.authorization.token,
					refreshToken: savedAuth.data.authorization.token,
				})
				this.user = savedAuth.data.user
				await Playback.setBearerToken({ bearerToken: `Bearer ${getAccessToken()}` })
				return true
			} else if (isLoggedIn()) {
				try {
					if (isPlatform('capacitor')) {
						const res = await this.requestRefresh(savedAuth.data.authorization.token);
						if (res === false) {
							return false;
						}
					}
					const { data = {} } = await api.get('/auth/user');
					if (Object.keys(data).length) {
						this.user = data;
						await Playback.setBearerToken({ bearerToken: `Bearer ${getAccessToken()}` })
						return true;
					}
				} catch (e) {
					try {
						setAuthTokens({
							accessToken: savedAuth.data.authorization.token,
							refreshToken: savedAuth.data.authorization.token,
						})
						this.user = savedAuth.data.user
						return true
					} catch (e) {
						return false
					}
				}
			}
			return false;
		},

		async login({ form, router }) {
			try {
				this.user = await login(form);
			} catch (err) {
				console.log('err -->', err);
				alert(err || `We're sorry. This doesn't seem to be working right now. Please try again later or contact support.`);
				logout()
				return;
			}

			this.redirect = getQuery() || {};
			if (this.hasRedirect) {
				router.push(this.redirect);
				this.redirect = {};
				return;
			} else {
				router.push({ name: 'list', params: { list: 'library' } });
				return;
			}
		},

		async logout() {
			setAuthTokens({
				accessToken: "",
				refreshToken: "",
			})
			await logout();

			try {
				localStorage.clear()
				await Preferences.clear();
	
				const sqlite = window.sqlite;
				const isConn = (await sqlite.isConnection("db", false)).result;
				let db;
				const ret = await sqlite.checkConnectionsConsistency();
				if (ret.result && isConn) {
					db = await sqlite.retrieveConnection("db", false);
				} else {
					db = await sqlite.createConnection("db", false, "no-encryption", 1, false);
				}
				await db.open()
	
				let tablesDb = (await db.query(`SELECT name FROM sqlite_master WHERE type='table'`)).values
				let tables = tablesDb.map(row => row.name)
				for (let table of tables) {
					try {
						await db.query(`DROP TABLE ${table}`)
					} catch (e) { }
				}
			} catch (e) {
				console.error('logout error', e);
			}


			if (isLoggedIn()) return alert('Please try again!');

			const audioStore = useAudioStore();
			audioStore.beforeStoreReset();
			//TODO: Update the UI to hide the player before navigating to the login page

			// map through that list and use the **$reset** fn to reset the state
			getActivePinia()._s.forEach((store) => store.$reset());

			// router.push({ name: 'login' });
			window.location.reload();
		},

		async requestRefresh(refresh) {
			try {
				// Notice that this is the global axios instance, not the axiosInstance!  <-- important
				const response = await axios.post(`${process.env.VUE_APP_BASE_URL}/auth/refresh`, { refresh });

				await Preferences.set({
					key: 'auth',
					value: JSON.stringify(response),
				});
				await Playback.setBearerToken({ bearerToken: `Bearer ${response.data.authorization.token}` })
				setAuthTokens({
					accessToken: response.data.authorization.token,
					refreshToken: response.data.authorization.token,
				})
				return {
					accessToken: response.data.authorization.token,
					refreshToken: response.data.authorization.token,
				};
			}
			catch (e) {
				console.error('refresh error', e);
				this.logout();
				return false;
			}
		},

		async updateUser({ form }) {
			console.log('updateUser', form)
			let formData = {}
			if (form?.name) formData.name = form.name
			if (form?.email) formData.email = form.email
			const { data = {} } = await api.patch('/auth/user', formData);
			if (data.status === 'success') {
				if (data?.user) {
					this.user = data.user;
					if (form?.email && !this.user?.email_verified_at) {
						alert('Please verify your new email address. A verification email has been sent to you.')
					}
				}
				return true;
			} else {
				alert(data.message || 'An error occurred. Please try again.');
				return data.errors || {};
			}
		},

		async register({ form, router }) {
			const { data } = await api.post('/auth/register', form);

			if (data?.errors) {
				return data;
			}

			return this.login({ form: { email: form.email, password: form.password }, router });
		},

		async resetPassword({ form }) {
			console.log('TODO: resetPassword', form);
		},

		async updatePassword({ form }) {
			let formData = {}
			if (form?.password) formData.password = form.password
			if (form?.new_password) formData.new_password = form.new_password
			if (form?.confirm_password) formData.confirm_password = form.confirm_password

			const { data = {} } = await api.post('/auth/reset-password', formData);
			if (data.status === 'success') {
				alert('Password updated successfully');
				return true;
			} else {
				alert(data.message || 'An error occurred. Please try again.');
				return false;
			}
		},

		async confirmDelete({ router }) {
			if (confirm('Are you sure you want to delete your account? This action is irreversible.')) {
				const { data = {} } = await api.delete('/auth/user');
				if (data.status === 'success') {
					alert('Account deleted successfully');
					this.logout();
					return true;
				} else {
					alert(data.message || 'An error occurred. Please try again.');
					return false;
				}
			}
		},

		async resendVerificationEmail() {
			if (isLoggedIn()) {
				const { data = {} } = await api.post('/auth/resend-verification-email');
				if (data.status === 'success') {
					alert('Verification email sent successfully');
					return true;
				} else {
					alert(data.message || 'An error occurred. Please try again.');
					return false;
				}
			} else {
				this.logout();
				alert('Please login first.');
				window.location.reload();
			}
		},

		async initialize() {
      await this.loadActiveProfile();
      await this.checkBiometryAvailability();
      await this.setupResumeListener();
      await this.loadPinAttempts();
    },

    async loadActiveProfile() {
      const { value } = await Preferences.get({ key: ACTIVE_PROFILE_KEY });
      this.activeProfileId = value ? parseInt(value, 10) : null;
    },

    async checkBiometryAvailability() {
      try {
        this.biometryInfo = await BiometricAuth.checkBiometry();
      } catch (error) {
        console.error('Error checking biometry availability:', error);
        this.biometryInfo = { isAvailable: false } as CheckBiometryResult;
      }
    },

    async setupResumeListener() {
      await BiometricAuth.addResumeListener((info: CheckBiometryResult) => {
        this.biometryInfo = info;
      });
    },

    async loadPinAttempts() {
      const { value: attempts } = await Preferences.get({ key: PIN_ATTEMPTS_KEY });
      const { value: lockoutTime } = await Preferences.get({ key: PIN_LOCKOUT_TIME_KEY });
      this.pinAttempts = attempts ? parseInt(attempts, 10) : 0;
      this.pinLockoutTime = lockoutTime ? new Date(lockoutTime) : null;

      if (this.pinLockoutTime) {
        if (new Date() > this.pinLockoutTime) {
          await this.resetPinAttempts();
        } else {
          this.setLockoutTimer();
        }
      }
    },

    setLockoutTimer() {
      if (this.lockoutTimer) {
        clearTimeout(this.lockoutTimer);
      }

      const remainingTime = new Date(this.pinLockoutTime).getTime() - Date.now();
      if (remainingTime > 0) {
        this.lockoutTimer = setTimeout(() => {
          this.resetPinAttempts();
        }, remainingTime);
      }
    },

    async switchProfile(profileId: number | null) {
      const currentProfile = this.activeProfile;
      const targetProfile = this.profiles.find(profile => profile.id === profileId);

      const isWeb = isPlatform('web');
      const isBiometryAvailable = this.biometryInfo?.isAvailable;

      // Check if leaving a restricted profile
      if (currentProfile?.is_restricted && this.user.has_pin) {
        if (isWeb || !isBiometryAvailable) {
          await this.verifyUserPin();
        } else {
          await this.authenticateWithBiometrics();
        }
      }

      // No need to check when switching TO a restricted profile

      await this.setActiveProfile(profileId);
      window.location.reload();
    },

    async verifyUserPin() {
			if (this.isPinLocked) {
				const remainingTime = Math.max(0, Math.ceil((new Date(this.pinLockoutTime).getTime() - Date.now()) / 1000));
				throw new Error(`Too many attempts. Please try again in ${remainingTime} seconds.`);
			}
		
			const pin = prompt('Please enter your 4-digit PIN to switch profiles:');
			if (!pin) {
				throw new Error('PIN is required to switch profiles.');
			}
		
			if (!/^\d{4}$/.test(pin)) {
				await this.incrementPinAttempts();
				throw new Error('Invalid PIN. Please enter a 4-digit number.');
			}
		
			try {
				const response = await api.post('/auth/verify-switch', { pin });
				if (response.data?.status !== 'success') {
					throw new Error('Invalid PIN. Please try again.');
				}
				await this.resetPinAttempts();
			} catch (error) {
				await this.incrementPinAttempts();
				if (error.response && error.response.status === 401) {
					throw new Error('Invalid PIN. Please try again.');
				} else {
					throw new Error('An error occurred while verifying the PIN. Please try again.');
				}
			}
		},

    async incrementPinAttempts() {
      this.pinAttempts++;
      await Preferences.set({ key: PIN_ATTEMPTS_KEY, value: this.pinAttempts.toString() });

      if (this.pinAttempts >= 3) {
        const lockoutTime = new Date(Date.now() + 60 * 1000); // 1 minute lockout
        this.pinLockoutTime = lockoutTime;
        await Preferences.set({ key: PIN_LOCKOUT_TIME_KEY, value: lockoutTime.toISOString() });
        this.setLockoutTimer();
      }
    },

    async resetPinAttempts() {
      this.pinAttempts = 0;
      this.pinLockoutTime = null;
      if (this.lockoutTimer) {
        clearTimeout(this.lockoutTimer);
        this.lockoutTimer = null;
      }
      await Preferences.remove({ key: PIN_ATTEMPTS_KEY });
      await Preferences.remove({ key: PIN_LOCKOUT_TIME_KEY });
    },

    async authenticateWithBiometrics() {
      const options: AuthenticateOptions = {
        reason: 'Please authenticate to switch profiles',
        cancelTitle: 'Cancel',
        allowDeviceCredential: true,
        iosFallbackTitle: 'Use passcode',
        androidTitle: 'Biometric Authentication',
        androidSubtitle: 'Authenticate to switch profiles',
        androidConfirmationRequired: false,
      };

      try {
        await BiometricAuth.authenticate(options);
      } catch (error) {
        if (error instanceof Error && 'code' in error) {
          const biometryError = error as { code: BiometryErrorType, message: string };
          if (biometryError.code !== BiometryErrorType.userCancel) {
            throw new Error(`Biometric authentication failed: ${biometryError.message}`);
          } else {
            throw new Error('Authentication cancelled.');
          }
        } else {
          throw new Error('An unexpected error occurred during authentication.');
        }
      }
    },

    async setActiveProfile(profileId: number | null) {
      this.activeProfileId = profileId;
      await Preferences.set({ key: ACTIVE_PROFILE_KEY, value: profileId ? profileId.toString() : '' });
    },

	},
});
